import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class AddScriptService {
  private document = inject(DOCUMENT) as Document;

  /**
   * Adiciona um <script> externo via URL no head ou body.
   * Faz dedupe por id (se informado) ou por src.
   */
  addExternalScript(
    src: string,
    target: 'head' | 'body' = 'head',
    options?: { id?: string; async?: boolean; defer?: boolean; type?: string; charset?: string }
  ): Promise<HTMLScriptElement> {
    return new Promise((resolve, reject) => {
      const doc = this.document;
      if (!doc) {
        reject(new Error('Documento indisponível'));
        return;
      }

      if (options?.id) {
        const existingById = doc.getElementById(options.id) as HTMLScriptElement | null;
        if (existingById) {
          resolve(existingById);
          return;
        }
      }

      const existingBySrc = Array.from(doc.getElementsByTagName('script'))
        .find(s => (s as HTMLScriptElement).src === src) as HTMLScriptElement | undefined;
      if (existingBySrc) {
        resolve(existingBySrc);
        return;
      }

      const script = doc.createElement('script');
      if (options?.id) script.id = options.id;
      script.src = src;
      script.async = options?.async ?? true;
      if (options?.defer != null) script.defer = options.defer;
      script.type = options?.type ?? 'text/javascript';
      script.charset = options?.charset ?? 'utf-8';

      script.onload = () => resolve(script);
      script.onerror = () => reject(new Error(`Falha ao carregar script: ${src}`));

      this.getTarget(target).appendChild(script);
    });
  }

  /**
   * Adiciona um <script> inline no head ou body.
   * Se id for informado, faz dedupe por id.
   */
  addInlineScript(
    content: string,
    target: 'head' | 'body' = 'head',
    options?: { id?: string; type?: string }
  ): HTMLScriptElement {
    const doc = this.document;
    if (!doc) {
      throw new Error('Documento indisponível');
    }
    if (options?.id) {
      const existing = doc.getElementById(options.id) as HTMLScriptElement | null;
      if (existing) return existing;
    }

    const script = doc.createElement('script');
    if (options?.id) script.id = options.id;
    script.type = options?.type ?? 'text/javascript';
    script.text = content;
    this.getTarget(target).appendChild(script);
    return script;
  }

  /**
   * Adiciona um bloco <noscript> com um <iframe> dentro no body.
   * Útil para fallback do Google Tag Manager.
   */
  addNoScriptIframe(
    src: string,
    options?: { id?: string; height?: string | number; width?: string | number; style?: string }
  ): HTMLElement {
    const doc = this.document;
    if (!doc) {
      throw new Error('Documento indisponível');
    }
    if (options?.id) {
      const existing = doc.getElementById(options.id);
      if (existing) return existing;
    }

    const noscript = doc.createElement('noscript');
    if (options?.id) noscript.id = options.id as string;

    const iframe = doc.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('height', String(options?.height ?? 0));
    iframe.setAttribute('width', String(options?.width ?? 0));
    iframe.setAttribute('style', options?.style ?? 'display:none;visibility:hidden');

    noscript.appendChild(iframe);
    this.getTarget('body').appendChild(noscript);
    return noscript;
  }

  /**
   * Helper específico para Google Tag Manager (GTM).
   * Insere script no head e noscript no body.
   * Permite definir dinamicamente a URL base do servidor GTM.
   */
  addGoogleTagManager(
    containerId: string,
    options?: { baseUrl?: string; dataLayerName?: string }
  ): void {
    const inlineId = `gtm-inline-${containerId}`;
    const noscriptId = `gtm-noscript-${containerId}`;

    const dataLayerName = options?.dataLayerName ?? 'dataLayer';
    const baseUrlRaw = options?.baseUrl ?? 'https://sites-gtm.btgpactual.com';
    const baseUrl = baseUrlRaw.replace(/\/+$/, '');

    const inlineContent = `\
(function(w,d, s, l, i){\
w[l]=w[l]||[];\
w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});\
var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='${dataLayerName}'?'&l='+l:'';\
j.async=true;\
j.src='${baseUrl}/gtm.js?id='+i+dl;\
f.parentNode.insertBefore(j,f);\
})(window, document, 'script', '${dataLayerName}', '${containerId}');`;

    this.addInlineScript(inlineContent, 'head', { id: inlineId });
    this.addNoScriptIframe(`${baseUrl}/ns.html?id=${containerId}`, {
      id: noscriptId,
      height: 0,
      width: 0,
      style: 'display:none;visibility:hidden'
    });
  }

  /**
   * Método genérico para adicionar qualquer elemento com atributos arbitrários.
   */
  addElement<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    attributes: Record<string, string | number | boolean>,
    target: 'head' | 'body' = 'head'
  ): HTMLElementTagNameMap[K] {
    const el = this.document.createElement(tagName);
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'textContent') {
        (el as any).textContent = String(value);
      } else if (key in el) {
        (el as any)[key] = value as any;
      } else {
        el.setAttribute(key, String(value));
      }
    });
    this.getTarget(target).appendChild(el);
    return el as any;
  }

  private getTarget(target: 'head' | 'body'): HTMLElement {
    return (target === 'head' ? this.document.head : this.document.body) as HTMLElement;
  }
}
